import "./Dashboard.css";

import { FullRightArrowDark } from "../../../assets";
import { DeveloperTemplate } from "../../../templates";
import { UpcomingMilestonesTaskPanel } from "../../../templates/UpcomingMilestonesTaskPanel";
import { WorkflowTaskPanel } from "../../../templates/WorkflowTaskPanel";
import {
  Button,
  Column,
  CursorDataGrid,
  Divider,
  KanaTooltip,
  Map,
  OverdueTasks,
  Row,
  UnitsKPI,
  UpcomingMilestones,
  UserHeader,
} from "../../../widget";
import { DashboardPieCard } from "../../shared/components";
import { TeamTasksPanel, YourTasksPanel } from "./components";
import { useDashboard } from "./useDashboard";

const Dashboard = (): JSX.Element => {
  const {
    tasksDataLoad,
    teamTasksDataLoad,
    columnsTasks,
    columnsTeamTasks,
    isLoading,
    overdueTasks,
    phaseChartData,
    isTasks,
    isTeamTasks,
    upcomingMilestones,
    projectsCoordinates,
    isUpcomingMilestonesPanelOpen,
    setIsUpcomingMilestonesPanelOpen,
    isYourTasksPanelOpen,
    setIsYourTasksPanelOpen,
    isTeamTasksPanelOpen,
    setIsTeamTasksPanelOpen,
    workflow,
    selectedPanelTask,
    showWorkflowTaskPanel,
    handleCloseWorkflowTaskPanel,
    handleRefresh,
    refreshTasks,
    setSelectedPanelTask,
    setShowWorkflowTaskPanel,
    dropDownData,
    onSelectChange,
    unitsKey,
  } = useDashboard();
  return (
    <DeveloperTemplate activeElement="Dashboard">
      <div className="DevelopmentManagerDashboard">
        <Row spacingV="ml">
          <Column span={12}>
            <UserHeader />
          </Column>
        </Row>
        <Row spacingV="ml">
          <Column span={12}>
            <div className="HeaderContainer">
              <h1>Dashboard</h1>
            </div>
          </Column>
        </Row>

        {/* Loading state --------- */}
        {isLoading && (
          <Row spacingV="ml">
            <Column span={6}>
              <DashboardPieCard
                title="Project phase"
                data={{}}
                tooltip={
                  <KanaTooltip
                    tooltipHeader="Project Phase:"
                    tooltipText="Shows the number of projects by phases of their workflow."
                  />
                }
                isGradient
                centerTextFont="Fraunces"
                colours="roadmap"
              />
            </Column>
            <Column span={6}>
              <div className="DLoadingCard">
                <p className="body01">Inventory</p>
                <p className="body2 NoData">Loading...</p>
              </div>
            </Column>
          </Row>
        )}

        {!isLoading && (
          <Row spacingV="ml">
            <Column span={6}>
              <DashboardPieCard
                title="Project phase"
                data={phaseChartData}
                tooltip={
                  <KanaTooltip
                    tooltipHeader="Project phase:"
                    tooltipText="Shows the number of projects by phases of their workflow."
                  />
                }
                isGradient
                centerTextFont="Fraunces"
                colours="roadmap"
              />
            </Column>
            <Column span={6}>
              <UnitsKPI
                unitsKey={unitsKey}
                dropdownOptions={dropDownData}
                showTooltip={false}
                title="Inventory"
                onSelectChange={onSelectChange}
              />
            </Column>
          </Row>
        )}
        <Row spacingV="ml" className="CardsRow">
          <Column span={8} className="CardsColumn">
            <div className="MilestoneCard">
              <UpcomingMilestones
                milestones={upcomingMilestones}
                onViewAll={() => setIsUpcomingMilestonesPanelOpen(true)}
                variant="dashboard"
              />
            </div>
            <div className={`TaskTableContainer${!isTasks ? " TaskTableContainer--empty" : ""}`}>
              <div className="TaskTableHeader">
                <div className="TaskTableHeaderAndIcon">
                  <p className="body01">Your tasks</p>
                  <KanaTooltip
                    tooltipHeader="Your tasks:"
                    tooltipText="View and manage the tasks that are assigned to you."
                  />
                </div>
                <div className="TaskViewAllButton">
                  <Button
                    text="View more"
                    onClick={() => setIsYourTasksPanelOpen(true)}
                    variant="secondary"
                    icon={<FullRightArrowDark />}
                    minWidth={false}
                    hasNoPadding
                  />
                </div>
              </div>
              <div className="TaskTableWrapper">
                <CursorDataGrid
                  data={[]}
                  columns={columnsTasks}
                  clientSide={false}
                  onChange={tasksDataLoad}
                  dataIsLoading={isLoading}
                  noDataMessage="No tasks to display"
                  showTotalCount={false}
                  hasBottomMargin={false}
                  sortable={false}
                  refresh={refreshTasks}
                  hasNoPadding
                  hasScrollButton={false}
                />
              </div>
            </div>
            <div className={`TaskTableContainer${!isTeamTasks ? " TaskTableContainer--empty" : ""}`}>
              <div className="TaskTableHeader">
                <div className="TaskTableHeaderAndIcon">
                  <p className="body01">Team tasks</p>
                  <KanaTooltip
                    tooltipHeader="Team tasks:"
                    tooltipText="See all tasks assigned to your team and support organisations in one place."
                  />
                </div>
                <Button
                  text="View more"
                  onClick={() => setIsTeamTasksPanelOpen(true)}
                  variant="secondary"
                  icon={<FullRightArrowDark />}
                  minWidth={false}
                  hasNoPadding
                />
              </div>
              <Divider type="horizontal" />
              <div className="OverdueTasksContainer">
                <OverdueTasks
                  label="Overdue tasks"
                  amount={overdueTasks.amount}
                  variant="dashboard"
                  onViewAll={alert}
                />
              </div>
              <Divider type="horizontal" className="BottomDivider" />
              <div className="TaskTableWrapper">
                <CursorDataGrid
                  data={[]}
                  columns={columnsTeamTasks}
                  clientSide={false}
                  onChange={teamTasksDataLoad}
                  dataIsLoading={isLoading}
                  noDataMessage="No tasks to display"
                  showTotalCount={false}
                  hasBottomMargin={false}
                  refresh={refreshTasks}
                  sortable={false}
                  hasNoPadding
                  hasScrollButton={false}
                />
              </div>
            </div>
          </Column>
          <Column span={4}>
            <Map mode="terrain" height={623} coordinates={projectsCoordinates ?? []} fullWidth />
          </Column>
        </Row>
        <div>
          <YourTasksPanel
            show={isYourTasksPanelOpen}
            onClose={() => setIsYourTasksPanelOpen(false)}
            setSelectedPanelTask={setSelectedPanelTask}
            setShowWorkflowTaskPanel={setShowWorkflowTaskPanel}
            refreshTasks={refreshTasks}
          />
          <TeamTasksPanel
            show={isTeamTasksPanelOpen}
            onClose={() => setIsTeamTasksPanelOpen(false)}
            setSelectedPanelTask={setSelectedPanelTask}
            setShowWorkflowTaskPanel={setShowWorkflowTaskPanel}
            refreshTasks={refreshTasks}
          />
          <UpcomingMilestonesTaskPanel
            show={isUpcomingMilestonesPanelOpen}
            isDashboard
            onClose={() => setIsUpcomingMilestonesPanelOpen(false)}
            setSelectedPanelTask={setSelectedPanelTask}
            setShowWorkflowTaskPanel={setShowWorkflowTaskPanel}
            refreshTasks={refreshTasks}
          />
          <WorkflowTaskPanel
            task={selectedPanelTask}
            currentStepUuid={selectedPanelTask?.step.uuid}
            workflow={workflow}
            show={showWorkflowTaskPanel}
            refresh={refreshTasks}
            handleRefresh={handleRefresh}
            onClose={handleCloseWorkflowTaskPanel}
          />
        </div>
      </div>
    </DeveloperTemplate>
  );
};

export default Dashboard;
